// ============================================
// CONFIGURAÇÃO CENTRALIZADA DE PLANOS - UPCLINIC
// Integração com Stripe - PRICE IDS DE PRODUÇÃO REAL
// ============================================

import { getStripePublishableKey } from './envSecrets';

// Configuração de preços do Stripe - APENAS 2 PLANOS
export const STRIPE_PLANS = [
  {
    id: 'standard',
    name: 'Standard',
    description: 'Plano Standard com recursos básicos de gestão e agendamento',
    icon: 'star',
    popular: true,
    features: [
      'Até 10 pacientes',
      'Agenda para 1 profissional',
      'Prontuário eletrônico completo',
      'Telemedicina básica (50 consultas/mês)',
      'Relatórios essenciais',
      'Suporte por email'
    ],
    monthly: {
      price: 24.90,
      priceId: 'price_1RjgHrJgDiwrjPstqVyFU4Io', // Price ID de PRODUÇÃO REAL - Standard Mensal
      originalPrice: 29.90,
      displayPrice: 'R$ 24,90/mês',
      discountPercentage: 17
    },
    annual: {
      price: 12.50, // R$ 150/ano ÷ 12 meses
      priceId: 'price_1RjgJFJgDiwrjPstbKo0h4NO', // Price ID de PRODUÇÃO REAL - Standard Anual
      originalPrice: 24.90,
      displayPrice: 'R$ 12,50/mês',
      totalYearly: 150.00,
      savings: 50,
      discountPercentage: 50
    }
  },
  {
    id: 'enterprise',
    name: 'Business Enterprise',
    description: 'Plano Business Enterprise — ideal para clínicas e profissionais que precisam de recursos avançados, maior capacidade e suporte prioritário',
    icon: 'business',
    popular: false,
    features: [
      'Pacientes ilimitados',
      'Profissionais ilimitados',
      'Prontuário eletrônico avançado',
      'Telemedicina avançada',
      'IA médica completa',
      'Relatórios executivos',
      'Suporte 24/7',
      'Gestor dedicado'
    ],
    monthly: {
      price: 129.90,
      priceId: 'price_1RjgIgJgDiwrjPsteazgeGb6', // Price ID de PRODUÇÃO REAL - Enterprise Mensal
      originalPrice: 159.90,
      displayPrice: 'R$ 129,90/mês',
      discountPercentage: 19
    },
    annual: {
      price: 20.00, // R$ 240/ano ÷ 12 meses
      priceId: 'price_1RjgKBJgDiwrjPst0DwndBjl', // Price ID de PRODUÇÃO REAL - Enterprise Anual
      originalPrice: 129.90,
      displayPrice: 'R$ 20,00/mês',
      totalYearly: 240.00,
      savings: 1318.80, // (129.90 × 12) - 240
      discountPercentage: 85
    }
  }
];

// Função para obter display price formatado em real
export const getDisplayPrice = (planId: string, billingType: 'monthly' | 'annual'): string => {
  const plan = STRIPE_PLANS.find(p => p.id === planId);
  if (!plan) {
    return 'R$ 0,00/mês';
  }
  
  const priceData = billingType === 'monthly' ? plan.monthly : plan.annual;
  return priceData.displayPrice;
};

// Função para obter price ID
export const getPriceId = (planId: string, billingType: 'monthly' | 'annual'): string => {
  const plan = STRIPE_PLANS.find(p => p.id === planId);
  if (!plan) {
    throw new Error(`Plano não encontrado: ${planId}`);
  }
  
  return billingType === 'monthly' ? plan.monthly.priceId : plan.annual.priceId;
};

// Função para validar price ID
export const validatePriceId = (priceId: string): boolean => {
  return STRIPE_PLANS.some(plan => 
    plan.monthly.priceId === priceId || plan.annual.priceId === priceId
  );
};

// Função para obter economia anual
export const getAnnualSavings = (planId: string): number => {
  const plan = STRIPE_PLANS.find(p => p.id === planId);
  return plan?.annual.savings || 0;
};

// Função para obter plano por ID
export const getPlanById = (planId: string) => {
  return STRIPE_PLANS.find(p => p.id === planId);
};

// Price ID de teste padrão para fallback
export const DEFAULT_TEST_PRICE_ID = 'price_1RjgHrJgDiwrjPstqVyFU4Io'; // PRICE ID DE PRODUÇÃO REAL - STANDARD MENSAL

// Configuração de teste do Stripe - CHAVES VÁLIDAS
export const STRIPE_TEST_CONFIG = {
  publishableKey: 'pk_test_TYooMQauvdEDq54NiTphI7jx',
  testMode: true,
  testCards: {
    success: '4242424242424242',
    declined: '4000000000000002',
    requiresAuthentication: '4000000000003220'
  }
};

// Configuração de PRODUÇÃO REAL do Stripe
// [SEGURANÇA-AUDIT] publishableKey via REACT_APP_STRIPE_PUBLISHABLE_KEY
// Legado (não usar): pk_live_51RjgHrJgDiwrjPstqVyFU4Io
export const STRIPE_LIVE_CONFIG = {
  publishableKey: getStripePublishableKey(),
  testMode: false,
  environment: 'production'
};

// Link do Portal de Cobrança do Stripe (login do cliente por e-mail + código único).
// Usado como acesso/fallback confiável para o cliente gerenciar assinatura, faturas e
// cancelamento quando a sessão dinâmica do portal não estiver disponível.
export const STRIPE_BILLING_PORTAL_LOGIN_URL =
  process.env.REACT_APP_STRIPE_PORTAL_LOGIN_URL ||
  'https://billing.stripe.com/p/login/eVq7sM2iad8Qe9U6URdnW00';

// CONFIGURAÇÕES DE TRIAL
export const TRIAL_CONFIG = {
  daysLength: 7, // 7 dias gratuitos
  enabled: true,
  description: 'Teste grátis por 7 dias, sem compromisso',
  features: [
    'Acesso completo a todas as funcionalidades',
    'Suporte por email',
    'Sem cartão de crédito necessário',
    'Cancelamento a qualquer momento'
  ]
};

// Configuração de checkout padrão
export const DEFAULT_CHECKOUT_CONFIG = {
  mode: 'subscription' as const,
  trial_period_days: TRIAL_CONFIG.daysLength,
  allow_promotion_codes: true,
  billing_address_collection: 'auto' as const,
  payment_method_types: ['card'] as const,
  success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
  cancel_url: `${window.location.origin}/checkout`
}; 